import { Button, CardFooter, Spinner } from "@fluentui/react-components";

import { COPY_OFFER, CREATE_OFFER, OPEN_OFFER } from "library/shared";
import React from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { useFormContext } from "react-hook-form";
import {
  requiredFieldsForCalculation,
  requiredFieldsForCreateOffer,
  requiredFieldsForGenerateInvoice,
} from "./constants";
import { useCalculateCasco } from "hooks/data/mutations/useCalculateCasco";
import { Flex, RightSection } from "components/styled";
import { EMainTab, ESidemenuTab } from "pages/VehicleItem/types";
import { useVehicleFormContext } from "providers/VehicleFormProvider";
import {
  DocumentPdfRegular,
  DocumentTextRegular,
} from "@fluentui/react-icons";
import usePolicyValueValidation from "../hooks/usePolicyValueValidation";

function FormFooter() {
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const offerId = queryParams.get("offerId");

  const { watch, handleSubmit } = useFormContext();
  const formData = watch();

  const { isAllianzInsurer, maxPolicyValueCorrect } =
    usePolicyValueValidation(formData);

  const {
    onNextSideTab,
    activeSideTab,
    activeMainTab,
    onPrevSideTab,
    selectedInsurers,
    setInfoCalculationData,
  } = useVehicleFormContext();

  const { mutate: onCalculateCasco, isLoading: calculating } =
    useCalculateCasco();

  const actionType = queryParams.get("actionType");

  const isOpenOfferAction = actionType === OPEN_OFFER;
  const isCopyAction = actionType === COPY_OFFER;
  const isCreateAction = actionType === CREATE_OFFER;

  const showCalculationActionBtn = isCopyAction || isCreateAction;

  const hasPolicy = formData?.policyCascoId;

  const hasInvoice = formData?.invoiceCascoId;

  const calculateOfferFieldsFilled = requiredFieldsForCalculation.every(
    (field) => formData[field]
  );

  const createOfferFieldsFilled = requiredFieldsForCreateOffer.every(
    (field) => formData[field]
  );

  const generateInvoiceFieldsFilled = requiredFieldsForGenerateInvoice.every(
    (field) => formData[field]
  );

  const issuePolicyFieldFilled = formData?.validFrom;

  const showActionButtons = !isOpenOfferAction;

  const isFirstTab = activeSideTab === ESidemenuTab.general;
  const isLastSideTab = activeSideTab === ESidemenuTab.cascoOffer;
  const isInvoiceTab = activeMainTab === EMainTab.invoice;
  const isPolicyTab = activeMainTab === EMainTab.policy;
  const isOfferTab = activeMainTab === EMainTab.offer;
  const isOfferSideTab = activeSideTab === ESidemenuTab.cascoOffer;

  const showNextBtn = !isLastSideTab && !isInvoiceTab && !isPolicyTab;
  const showPrevBtn = !isFirstTab && !isInvoiceTab && !isPolicyTab;

  const showCalculateBtn =
    showActionButtons && showCalculationActionBtn && isOfferSideTab;

  const showCreateOfferBtn =
    isOfferSideTab && (isCopyAction ? !isInvoiceTab : !offerId);

  const showGenerateInvoiceBtn = isInvoiceTab && !hasInvoice && !hasPolicy;

  const showIssuePolicyBtn = hasInvoice && !hasPolicy && isPolicyTab;

  const showPdfOfferLink = formData?.pathToOfferPdf && isOfferTab;
  const showDocxOfferLink = formData?.pathToOfferDocx && isOfferTab;
  const showInvoicePdfLink = formData?.pathToInvoiceCascoPdf && isInvoiceTab;
  const showPolicyPdfLink = formData?.pathToPolicyCascoPdf && isPolicyTab;

  const onCalculate = (data: any) => {
    onCalculateCasco(
      { ...data, selectedCascoInsurers: selectedInsurers },
      {
        onSuccess(res) {
          setInfoCalculationData(res.dataObj);
        },
      }
    );
  };

  return (
    <CardFooter
      style={{
        flexDirection: "row",
        padding: "20px",
        borderTop: `1px solid ${theme.palette.titanWhite}`,
      }}
    >
      {showDocxOfferLink && (
        <Button
          as="a"
          appearance="secondary"
          href={formData?.pathToOfferDocx}
          target="_blank"
          icon={<DocumentTextRegular />}
        >
          {t("bcm.offerDocx.label")}
        </Button>
      )}
      {showPdfOfferLink && (
        <Button
          as="a"
          appearance="primary"
          href={formData?.pathToOfferPdf}
          target="_blank"
          icon={<DocumentPdfRegular />}
        >
          {t("bcm.offer.label")}
        </Button>
      )}
      {showInvoicePdfLink && (
        <Button
          as="a"
          appearance="primary"
          href={formData?.pathToInvoiceCascoPdf}
          target="_blank"
          icon={<DocumentPdfRegular />}
        >
          {t("bcm.invoice.label")}
        </Button>
      )}
      {showPolicyPdfLink && (
        <Button
          as="a"
          appearance="primary"
          href={formData?.pathToPolicyCascoPdf}
          target="_blank"
          icon={<DocumentPdfRegular />}
        >
          {t("bcm.policy.label")}
        </Button>
      )}
      <RightSection>
        <Flex gap={10}>
          {showPrevBtn && (
            <Button onClick={onPrevSideTab}>{t("bcm.previous.label")}</Button>
          )}
          {showNextBtn && (
            <Button onClick={onNextSideTab}>{t("bcm.next.label")}</Button>
          )}

          {showCalculateBtn && (
            <Button
              disabled={
                !calculateOfferFieldsFilled ||
                selectedInsurers.length === 0 ||
                calculating
              }
              appearance="primary"
              onClick={handleSubmit(onCalculate)}
              icon={calculating ? <Spinner size="tiny" /> : undefined}
            >
              {t("bcm.calculate.label")}
            </Button>
          )}

          {showCreateOfferBtn && (
            <Button
              disabled={
                !createOfferFieldsFilled || selectedInsurers.length === 0
              }
              appearance="primary"
              type="submit"
            >
              {t("bcm.getOffer.label")}
            </Button>
          )}
          {showGenerateInvoiceBtn && (
            <Button
              disabled={!generateInvoiceFieldsFilled}
              appearance="primary"
              type="submit"
            >
              {t("bcm.generateInvoice.label")}
            </Button>
          )}
          {showIssuePolicyBtn && (
            <Button
              disabled={
                !issuePolicyFieldFilled ||
                isAllianzInsurer ||
                !maxPolicyValueCorrect
              }
              appearance="primary"
              type="submit"
            >
              {t("bcm.issuePolicy.label")}
            </Button>
          )}
        </Flex>
      </RightSection>
    </CardFooter>
  );
}

export default FormFooter;
